import { type Dispatch, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import type { OnboardingStatus } from '../../lib/types';
// El mismo cálculo que ve el dueño en el alta pública. Dos cálculos de «qué
// falta» divergen: el del servidor ya estuvo roto sin que nada lo notara.
import { derivarEstado } from '../../pages/alta/EstadoDelAlta';
import { Button, Card, CardBody, CardFooter, CardHeader } from '../ui';
import type { WizardAction, WizardDraft } from './types';

export function Step6Summary({
  draft,
  dispatch,
  onActivate,
}: {
  draft: WizardDraft;
  dispatch: Dispatch<WizardAction>;
  onActivate: () => void;
}) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetchStatus = async () => {
      try {
        if (!draft.tenantId) return;
        const s = await api.tenants.getOnboardingStatus(draft.tenantId);
        if (mounted) setStatus(s);
      } catch (err) {
        console.error('Error fetching status', err);
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [draft.tenantId]);

  async function activate() {
    setActivating(true);
    setError(null);
    try {
      await api.tenants.setStatus(draft.tenantId as string, 'active');
      onActivate(); // limpiar localStorage + redirect a /tenants/:id
    } catch (err) {
      setError((err as Error).message);
      setActivating(false);
    }
  }

  const ready = status?.ready_to_activate ?? false;

  return (
    <Card data-testid="wizard-step-6">
      <CardHeader>
        <h2 className="text-h6">Paso 6 — Resumen y Activación</h2>
      </CardHeader>

      <CardBody className="space-y-6">
        <Card variant="default">
          <CardBody className="flex justify-between items-center p-4">
            <div>
              <h3 className="font-semibold text-content-primary">1. Identidad</h3>
              <p className="text-sm text-content-secondary mt-1">
                <span className="font-medium">{draft.tenantId}</span> — {draft.name}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => dispatch({ type: 'goto', step: 1 })}>
              Editar
            </Button>
          </CardBody>
        </Card>

        <Card variant="default">
          <CardBody className="flex justify-between items-center p-4">
            <div>
              <h3 className="font-semibold text-content-primary">2. Canales configurados</h3>
              <div className="text-sm text-content-secondary mt-2 space-y-1">
                <p>
                  Telegram:{' '}
                  {status?.channels?.telegram === 'done'
                    ? '✅ Conectado'
                    : status?.channels?.telegram === 'token-only'
                      ? '🔑 Token subido'
                      : '⏳ Pendiente'}
                </p>
                <p>
                  WhatsApp:{' '}
                  {status?.channels?.whatsapp === 'done'
                    ? '✅ Conectado'
                    : '⏳ Pendiente / Omitido'}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => dispatch({ type: 'goto', step: 2 })}>
              Editar canales
            </Button>
          </CardBody>
        </Card>

        <Card variant="default">
          <CardBody className="flex justify-between items-center p-4">
            <div>
              <h3 className="font-semibold text-content-primary">3. Integración ERP</h3>
              <p className="text-sm text-content-secondary mt-1 uppercase font-medium">
                {draft.erp?.type ?? '⏳ Pendiente'}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => dispatch({ type: 'goto', step: 4 })}>
              Editar ERP
            </Button>
          </CardBody>
        </Card>

        <Card variant="default">
          <CardBody className="flex justify-between items-center p-4">
            <div>
              <h3 className="font-semibold text-content-primary">4. Modo del Bot</h3>
              <p className="text-sm text-content-secondary mt-1 font-medium">
                {draft.botMode === 'informational'
                  ? 'Informativo'
                  : draft.botMode === 'transactional'
                    ? 'Transaccional'
                    : draft.botMode === 'hybrid'
                      ? 'Híbrido'
                      : '⏳ Pendiente'}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => dispatch({ type: 'goto', step: 5 })}>
              Editar Modo
            </Button>
          </CardBody>
        </Card>

        <Card variant="default">
          <CardBody className="flex justify-between items-center p-4">
            <div>
              <h3 className="font-semibold text-content-primary">5. Base de Conocimiento</h3>
              <p className="text-sm text-content-secondary mt-1 font-medium">
                {status?.kb === 'synced' && '✅ Sincronizado'}
                {status?.kb === 'unsynced' && '⏳ Indexando...'}
                {status?.kb === 'missing' && '❌ Falta crear KB'}
              </p>
              {status?.kbCheckError && (
                <p className="text-xs text-red-500 mt-1 font-normal">
                  ⚠️ Error verificando KB: {status.kbCheckError}
                </p>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/tenants/${draft.tenantId}`)}
            >
              {status?.kb === 'missing' ? 'Crear KB' : 'Editar KB'}
            </Button>
          </CardBody>
        </Card>

        {status && !ready && (
          <div className="toast toast-warning">
            <div className="toast-description">
              <p className="font-semibold mb-2">Faltan pasos obligatorios:</p>
              {/* Se derivan del estado. Antes se leían de `missing_steps`, un
                  campo que el endpoint NUNCA devolvió: el aviso decía «faltan
                  pasos obligatorios» encima de una lista vacía, y el operador
                  no tenía cómo saber cuáles. */}
              <ul className="list-disc pl-5">
                {derivarEstado(status)
                  .pasos.filter((paso) => !paso.completo)
                  .map((paso) => (
                    <li key={paso.id}>{paso.falta}</li>
                  ))}
              </ul>
            </div>
          </div>
        )}

        {error && (
          <div className="toast toast-error">
            <div className="toast-description">{error}</div>
          </div>
        )}
      </CardBody>

      <CardFooter className="flex pt-6 border-t border-edge-subtle">
        <Button
          variant={ready ? 'primary' : 'outline'}
          data-testid="activate-tenant-btn"
          onClick={activate}
          disabled={!ready || activating}
          loading={activating}
          className="w-full justify-center"
          size="lg"
        >
          {ready ? '🚀 Activar tenant' : 'Faltan datos para activar'}
        </Button>
      </CardFooter>
    </Card>
  );
}
